import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import NearMeIcon from '@mui/icons-material/NearMe';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Loader partagé avec MapboxAddressInput
let _mapsReady = false;
const _callbacks = [];
function ensureGoogleMaps(cb) {
  if (window.google?.maps) { cb(); return; }
  _callbacks.push(cb);
  if (document.querySelector('script[data-gmaps]')) return;
  const s = document.createElement('script');
  s.src = `https://maps.googleapis.com/maps/api/js?v=3.54&key=${GOOGLE_KEY}&libraries=places,geometry&language=fr`;
  s.async = true; s.defer = true;
  s.setAttribute('data-gmaps', '1');
  s.onload = () => { _mapsReady = true; _callbacks.forEach(f => f()); _callbacks.length = 0; };
  document.head.appendChild(s);
}

const MiniMap = ({
  latitude, longitude,
  pickupLat, pickupLng,
  dropoffLat, dropoffLng,
  height = 180,
  draggable = false,
  onDragEnd,
  onPickupDragEnd,
  onDropoffDragEnd,
  style, // ignoré (Google Maps gère le style)
  sx = {},
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const directionsRendererRef = useRef(null);

  const hasRoute = pickupLat && pickupLng && dropoffLat && dropoffLng;
  const singleLat = latitude || pickupLat;
  const singleLng = longitude || pickupLng;
  const isInteractive = draggable || !!onPickupDragEnd || !!onDropoffDragEnd;

  useEffect(() => {
    if (!containerRef.current) return;
    if (!singleLat && !hasRoute) return;

    ensureGoogleMaps(() => {
      if (!containerRef.current) return;

      const centerLat = hasRoute ? (pickupLat + dropoffLat) / 2 : singleLat;
      const centerLng = hasRoute ? (pickupLng + dropoffLng) / 2 : singleLng;

      const map = new window.google.maps.Map(containerRef.current, {
        center: { lat: centerLat, lng: centerLng },
        zoom: hasRoute ? 13 : 16,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        disableDefaultUI: !isInteractive,
        zoomControl: isInteractive,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        gestureHandling: isInteractive ? 'cooperative' : 'none',
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        ],
      });
      mapRef.current = map;

      if (hasRoute) {
        // Marqueur A — Départ
        pickupMarkerRef.current = new window.google.maps.Marker({
          position: { lat: pickupLat, lng: pickupLng },
          map,
          draggable: !!onPickupDragEnd,
          label: { text: 'A', color: 'white', fontWeight: 'bold', fontSize: '13px' },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#552583', fillOpacity: 1,
            strokeColor: 'white', strokeWeight: 3, scale: 14,
          },
        });

        // Marqueur B — Arrivée
        dropoffMarkerRef.current = new window.google.maps.Marker({
          position: { lat: dropoffLat, lng: dropoffLng },
          map,
          draggable: !!onDropoffDragEnd,
          label: { text: 'B', color: '#1a1a1a', fontWeight: 'bold', fontSize: '13px' },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#FDB927', fillOpacity: 1,
            strokeColor: 'white', strokeWeight: 3, scale: 14,
          },
        });

        // Tracer le trajet via Directions API
        const directionsService = new window.google.maps.DirectionsService();
        const directionsRenderer = new window.google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          polylineOptions: { strokeColor: '#552583', strokeWeight: 5, strokeOpacity: 0.85 },
        });
        directionsRendererRef.current = directionsRenderer;

        directionsService.route({
          origin: { lat: pickupLat, lng: pickupLng },
          destination: { lat: dropoffLat, lng: dropoffLng },
          travelMode: window.google.maps.TravelMode.DRIVING,
        }, (result, status) => {
          if (status === 'OK') {
            directionsRenderer.setDirections(result);
          }
        });

        // Fit bounds
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({ lat: pickupLat, lng: pickupLng });
        bounds.extend({ lat: dropoffLat, lng: dropoffLng });
        map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });

        // Drag events
        if (onPickupDragEnd && pickupMarkerRef.current) {
          pickupMarkerRef.current.addListener('dragend', (e) => {
            onPickupDragEnd({ latitude: e.latLng.lat(), longitude: e.latLng.lng() });
          });
        }
        if (onDropoffDragEnd && dropoffMarkerRef.current) {
          dropoffMarkerRef.current.addListener('dragend', (e) => {
            onDropoffDragEnd({ latitude: e.latLng.lat(), longitude: e.latLng.lng() });
          });
        }

      } else if (singleLat && singleLng) {
        markerRef.current = new window.google.maps.Marker({
          position: { lat: singleLat, lng: singleLng },
          map,
          draggable,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#552583', fillOpacity: 1,
            strokeColor: 'white', strokeWeight: 3, scale: 12,
          },
        });

        if (draggable && onDragEnd) {
          markerRef.current.addListener('dragend', (e) => {
            onDragEnd({ latitude: e.latLng.lat(), longitude: e.latLng.lng() });
          });
        }
      }
    });

    return () => {
      markerRef.current?.setMap(null);
      pickupMarkerRef.current?.setMap(null);
      dropoffMarkerRef.current?.setMap(null);
      directionsRendererRef.current?.setMap(null);
      mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleLat, singleLng, pickupLat, pickupLng, dropoffLat, dropoffLng]);

  if (!singleLat && !hasRoute) return null;

  const showDragHint = draggable || !!onPickupDragEnd || !!onDropoffDragEnd;

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <Box sx={{
        borderRadius: '14px', overflow: 'hidden', height,
        border: '1px solid rgba(85,37,131,0.12)',
        boxShadow: '0 4px 16px rgba(85,37,131,0.08)',
      }}>
        <Box ref={containerRef} sx={{ width: '100%', height: '100%' }} />
      </Box>
      {showDragHint && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.8, px: 0.5 }}>
          <NearMeIcon sx={{ fontSize: 13, color: '#552583', opacity: 0.6 }} />
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(85,37,131,0.5)', fontWeight: 600 }}>
            Déplacez le marqueur pour ajuster la position exacte
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MiniMap;
